import { useEffect, useState, useCallback } from 'react';

const slides = [
  {
    id: 'retail',
    label: 'Retail',
    headline: 'Stop Overpaying\nfor Payments.',
    subtext:
      'Most processors hide fees, delay payouts, and give you no real insight. We give you full visibility, faster cash flow, and infrastructure built around how your business actually operates.',
    image:
      '/images/slide-retail-02.jpg',
    stat: { value: 'Omnichannel', label: 'Unified Commerce' },
  },
  {
    id: 'restaurant',
    label: 'Hospitality',
    headline: 'Your Margins Are Thin.\nYour Fees Shouldn\'t Be.',
    subtext:
      'Most e-commerce processors charge you for every transaction and do nothing to stop cart abandonment. We optimize your checkout to convert — and show you where every dollar goes.',
    image:
      '/images/slide-restaurant-02.jpg',
    stat: { value: '99.99%', label: 'Uptime SLA' },
  },
  {
    id: 'ecommerce',
    label: 'E-Commerce',
    headline: 'Your Checkout Is\nLeaking Revenue.',
    subtext:
      'Most e-commerce processors charge you for every transaction and do nothing to stop cart abandonment. We optimize your checkout to convert — and show you where every dollar goes.',
    image:
      '/images/slide-ecom-03.jpg',
    stat: { value: '+34%', label: 'Avg. Conversion Lift' },
  },
  {
    id: 'mobile',
    label: 'Mobile',
    headline: 'Accept Payments\nAnywhere.',
    subtext:
      'Your business moves fast. Your payment infrastructure should too — with the same visibility, security, and reporting whether you\'re in the field or at a fixed location.',
    image:
      '/images/slide-mobile-03.jpg',
    stat: { value: '24/7', label: 'Dedicated Support' },
  },
];

const AUTOPLAY_INTERVAL = 5500;

export default function Hero() {
  const [current, setCurrent] = useState(0);
  const [visible, setVisible] = useState(false);
  const [transitioning, setTransitioning] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  const goTo = useCallback(
    (index: number) => {
      if (transitioning || index === current) return;
      setTransitioning(true);
      setTimeout(() => {
        setCurrent(index);
        setTransitioning(false);
      }, 400);
    },
    [current, transitioning]
  );

  const next = useCallback(() => {
    goTo((current + 1) % slides.length);
  }, [current, goTo]);

  useEffect(() => {
    const timer = setInterval(next, AUTOPLAY_INTERVAL);
    return () => clearInterval(timer);
  }, [next]);

  const slide = slides[current];

  return (
    <section className="relative w-full min-h-screen overflow-hidden">
      {/* Background Images — all preloaded, crossfade */}
      {slides.map((s, i) => (
        <div
          key={s.id}
          className="absolute inset-0 transition-opacity duration-700 ease-in-out"
          style={{ opacity: i === current ? 1 : 0 }}
        >
          <img
            src={s.image}
            alt={s.label}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}

      {/* Overlays */}
      <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse 70% 100% at 50% 50%, #1a5a9a 0%, #0d3a6e 35%, #061e45 70%, #030f24 100%)', opacity: 0.82 }} />
      <div className="absolute inset-0 bg-gradient-to-r from-[#061e45]/50 via-transparent to-transparent" />

      {/* Content */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-8 flex flex-col justify-center min-h-screen" style={{ paddingTop: '150px', paddingBottom: '25px' }}>
        <div
          className={`transition-all duration-1000 ease-out ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {/* Eyebrow */}
          <p
            className="text-[#d3b366] text-[10px] md:text-[10px] uppercase tracking-[0.3em] font-medium mb-8"
            style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
          >
            See Exactly Where Your Money Goes
          </p>

          {/* Headline — crossfade on slide change */}
          <div
            className="transition-opacity duration-400 ease-in-out"
            style={{ opacity: transitioning ? 0 : 1 }}
          >
            <h1
              className="text-white text-[38px] md:text-[72px] leading-[1.05] font-light mb-8 max-w-3xl"
              style={{ fontFamily: "'Playfair Display', serif", letterSpacing: '-0.01em', whiteSpace: 'pre-line' }}
            >
              {slide.headline}
            </h1>

            <p
              className="text-white/70 md:text-xl max-w-xl mb-12 text-[18px]"
              style={{ fontFamily: "'Google Sans Flex', sans-serif", fontWeight: 400 }}
            >
              {slide.subtext}
            </p>
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 mb-16">
            <a
              href="#solutions"
              className="whitespace-nowrap inline-flex items-center justify-center px-8 py-4 bg-[#d3b366] text-[#061e45] text-[12px] uppercase tracking-[0.18em] font-semibold hover:bg-[#a8894a] hover:text-white transition-colors duration-200 cursor-pointer"
              style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
            >
              Explore Solutions
            </a>

          </div>


        </div>


      </div>

      {/* Slide dots */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-3">
        {slides.map((s, i) => (
          <button
            key={s.id}
            onClick={() => goTo(i)}
            className="cursor-pointer transition-all duration-300"
            aria-label={`Go to ${s.label} slide`}
            style={{
              width: i === current ? '28px' : '8px',
              height: '8px',
              borderRadius: '9999px',
              backgroundColor: i === current ? '#d3b366' : 'rgba(255,255,255,0.3)',
            }}
          />
        ))}
      </div>

      {/* Progress bar */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-white/10">
        <div
          key={current}
          className="h-full bg-[#d3b366]"
          style={{
            animation: `heroProgress ${AUTOPLAY_INTERVAL}ms linear forwards`,
          }}
        />
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 right-8 flex flex-col items-center gap-2 opacity-40">
        <span
          className="text-white text-[10px] uppercase tracking-[0.2em]"
          style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
        >
          Scroll
        </span>
        <div className="w-px h-12 bg-white/40" />
      </div>

      <style>{`
        @keyframes heroProgress {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>
    </section>
  );
}
